/* global axios */

import Cookies from 'js-cookie';
import endPoints from './endPoints';
import {
  setAuthCredentials,
  clearCookiesOnLogout,
  deleteIndexedDBOnLogout,
} from '../store/utils/api';
import RudderStackHelper from '../helper/RudderStackHelper';
const rudderStack = new RudderStackHelper(RudderStackHelper.RUDDER_STACK_APP);
let initialNotify = false;

export default {
  login(creds) {
    return new Promise((resolve, reject) => {
      axios
        .post('auth/sign_in', creds)
        .then(response => {
          setAuthCredentials(response);
          resolve(response.data);
        })
        .catch(error => {
          reject(error.response);
        });
    });
  },

  register(creds, campaignParams) {
    const urlData = endPoints('register');
    const fetchPromise = new Promise((resolve, reject) => {
      axios
        .post(urlData.url, {
          account_name: creds.accountName.trim(),
          user_full_name: creds.fullName.trim(),
          email: creds.email,
          password: creds.password,
          h_captcha_client_response: creds.hCaptchaClientResponse,
          campaignParams,
        })
        .then(response => {
          setAuthCredentials(response);
          rudderStack.identify(response?.data?.data?.id, {
            name: response?.data?.data?.name,
            email: response?.data?.data?.email,
          });
          resolve(response);
        })
        .catch(error => {
          reject(error);
        });
    });
    return fetchPromise;
  },
  validityCheck() {
    const urlData = endPoints('validityCheck');
    return axios.get(urlData.url);
  },
  logout() {
    const urlData = endPoints('logout');
    const fetchPromise = new Promise((resolve, reject) => {
      axios
        .delete(urlData.url)
        .then(response => {
          deleteIndexedDBOnLogout();
          clearCookiesOnLogout();
          resolve(response);
        })
        .catch(error => {
          reject(error);
        });
    });
    return fetchPromise;
  },
  hasAuthCookie() {
    return !!Cookies.getJSON('cw_d_session_info');
  },
  getAuthData() {
    if (this.hasAuthCookie()) {
      return Cookies.getJSON('cw_d_session_info');
    }
    return false;
  },
  getAuthToken() {
    if (this.hasAuthCookie()) {
      const sessionInfo = Cookies.getJSON('cw_d_session_info');
      const token = JSON.stringify({
        'access-token': sessionInfo['access-token'],
        'token-type': sessionInfo['token-type'],
        client: sessionInfo.client,
        expiry: sessionInfo.expiry,
        uid: sessionInfo.uid,
      });
      return btoa(token);
    }
    return null;
  },

  checkLoggedOut() {
    const sessionInfo = Cookies.getJSON('cw_d_session_info');
    if (sessionInfo && sessionInfo.client) {
      axios
        .post('auth/sessions/notify_if_logged_out', {
          client: sessionInfo.client,
        })
        .then(({ data }) => {
          if (!data) return;

          switch (data.status) {
            case 'notify':
              if (!initialNotify) {
                window.WOOT.$store.dispatch(
                  'userNotificationSettings/setSessionNotify',
                  true
                );
                initialNotify = true;
              }
              break;

            case 'do_not_notify':
              if (!initialNotify) {
                window.location.reload();
                initialNotify = true;
              }
              break;

            case 'still_logged_in': {
              const { name } = window.WOOT.$route;
              if (name !== 'home') window.WOOT.$router.push({ name: 'home' });
              break;
            }

            default:
              break;
          }
        })
        .catch(error => {
          window.bus.$emit('newToastMessage', error, 'error');
        });
    }
  },

  verifyPasswordToken({ confirmationToken }) {
    return new Promise((resolve, reject) => {
      axios
        .post('auth/confirmation', {
          confirmation_token: confirmationToken,
        })
        .then(response => {
          setAuthCredentials(response);
          resolve(response);
        })
        .catch(error => {
          reject(error.response);
        });
    });
  },

  setNewPassword({ resetPasswordToken, password, confirmPassword }) {
    return new Promise((resolve, reject) => {
      axios
        .put('auth/password', {
          reset_password_token: resetPasswordToken,
          password_confirmation: confirmPassword,
          password,
        })
        .then(response => {
          setAuthCredentials(response);
          resolve(response);
        })
        .catch(error => {
          reject(error.response);
        });
    });
  },

  resetPassword({ email }) {
    const urlData = endPoints('resetPassword');
    return axios.post(urlData.url, { email });
  },

  profileUpdate({
    password,
    password_confirmation,
    displayName,
    avatar,
    ...profileAttributes
  }) {
    const formData = new FormData();
    Object.keys(profileAttributes).forEach(key => {
      const hasValue = profileAttributes[key] === undefined;
      if (!hasValue) {
        formData.append(`profile[${key}]`, profileAttributes[key]);
      }
    });
    formData.append('profile[display_name]', displayName || '');
    if (password && password_confirmation) {
      formData.append('profile[password]', password);
      formData.append('profile[password_confirmation]', password_confirmation);
    }
    if (avatar) {
      formData.append('profile[avatar]', avatar);
    }
    return axios.put(endPoints('profileUpdate').url, formData);
  },
  // TODO: remove and updateUISettings, make it dynamic
  updateUISettings({ uiSettings }) {
    return axios.put(endPoints('profileUpdate').url, {
      profile: { ui_settings: uiSettings },
    });
  },

  updateProfileInfo(params) {
    return axios.put(endPoints('profileUpdate').url, {
      profile: params,
    });
  },

  updateAvailability(availabilityData) {
    return axios.post(endPoints('availabilityUpdate').url, {
      profile: { ...availabilityData },
    });
  },

  updateAutoOffline(accountId, autoOffline = false) {
    return axios.post(endPoints('autoOffline').url, {
      profile: { account_id: accountId, auto_offline: autoOffline },
    });
  },

  updateCanReceiveIncomingCalls(accountId, canReceiveIncomingCalls = false) {
    return axios.post(endPoints('receiveIncomingCalls').url, {
      profile: {
        account_id: accountId,
        receive_incoming_calls: canReceiveIncomingCalls,
      },
    });
  },

  deleteAvatar() {
    return axios.delete(endPoints('deleteAvatar').url);
  },

  setActiveAccount({ accountId }) {
    const urlData = endPoints('setActiveAccount');
    return axios.put(urlData.url, {
      profile: {
        account_id: accountId,
      },
    });
  },
};
